import React from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
    FormHelperText,
} from '@mui/material';
import { trakrefBlue, Grey3, red, focusColor } from '../../../color_utils';
import { trakrefFontFamily } from '../../../utils';

const SegmentedButtonGroup = ({
    label,
    required,
    value,
    onChange,
    options,
    error,
    helperText,
    disabled,
    size,
    sx,
}) => {
    const handleChange = (_event, newValue) => {
        if (newValue === null || newValue === value) return;
        onChange(newValue);
    };

    const borderColor = error ? red : trakrefBlue;
    const unselectedColor = error ? red : Grey3;

    return (
        <Box sx={{ width: '100%', ...sx }}>
            {label && (
                <Typography
                    variant="body2"
                    sx={{
                        fontFamily: trakrefFontFamily,
                        color: error ? red : Grey3,
                        marginBottom: '4px',
                    }}
                >
                    {label}
                    {required && (
                        <Typography
                            component="span"
                            sx={{ color: red, marginLeft: '4px' }}
                        >
                            *
                        </Typography>
                    )}
                </Typography>
            )}
            <ToggleButtonGroup
                exclusive
                value={value ?? null}
                onChange={handleChange}
                size={size}
                disabled={disabled}
                aria-label={label || 'segmented-button-group'}
            >
                {options.map(opt => (
                    <ToggleButton
                        key={String(opt.value)}
                        value={opt.value}
                        disabled={opt.disabled}
                        sx={{
                            fontFamily: trakrefFontFamily,
                            textTransform: 'none',
                            color: unselectedColor,
                            borderColor: unselectedColor,
                            '&.Mui-selected': {
                                color: borderColor,
                                borderColor: borderColor,
                                backgroundColor: 'transparent',
                            },
                            '&.Mui-selected:hover': { backgroundColor: 'transparent' },
                            '&:focus': { outline: `3px solid ${focusColor}` },
                            '&.Mui-disabled': {
                                opacity: 0.5,
                                pointerEvents: 'none',
                            },
                        }}
                    >
                        {opt.label}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
            {error && helperText && (
                <FormHelperText
                    sx={{ color: red, fontFamily: trakrefFontFamily, marginLeft: 0 }}
                >
                    {helperText}
                </FormHelperText>
            )}
        </Box>
    );
};

SegmentedButtonGroup.propTypes = {
    label: PropTypes.string,
    required: PropTypes.bool,
    value: PropTypes.any,
    onChange: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.any.isRequired,
        label: PropTypes.string.isRequired,
        disabled: PropTypes.bool,
    })).isRequired,
    error: PropTypes.bool,
    helperText: PropTypes.string,
    disabled: PropTypes.bool,
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    sx: PropTypes.object,
};

SegmentedButtonGroup.defaultProps = {
    label: '',
    required: false,
    value: null,
    error: false,
    helperText: '',
    disabled: false,
    size: 'medium',
    sx: {},
};

export default SegmentedButtonGroup;
